import { useEffect, useRef, useState } from "react";
import { BotMascot, type ActId, type BotMascotHandle, type MoodId } from "@myela/bot-mascot-react";
import "@myela/bot-mascot-react/styles.css";

// Ela as the Dashboard draws her (payments-dashboard components/assistant/ElaBot.tsx):
// still at rest, awake while the control holding her is hovered or focused, and
// thinking while a reply is coming. Reduced motion keeps her still.

export type ElaActivity = "idle" | "listening" | "thinking";

const BRAND = { body: "var(--accent-600)", eyes: "var(--ink-900)" };
const MOOD: Record<ElaActivity, MoodId> = { idle: "ready", listening: "curious", thinking: "thinking" };

export function ElaBot({
  size = 44,
  activity = "idle",
  greeting = "wave",
  still = false,
  label = "Ela",
}: {
  /** Width in px; the height follows the mascot's 280:150 ratio. */
  size?: number;
  activity?: ElaActivity;
  greeting?: ActId;
  /** Never animate. */
  still?: boolean;
  label?: string;
}) {
  const wrap = useRef<HTMLSpanElement>(null);
  const bot = useRef<BotMascotHandle>(null);
  const [awake, setAwake] = useState(false);

  useEffect(() => {
    if (still) return;
    const host = wrap.current?.closest("form, button, a");
    if (!host) return;
    const wake = () => setAwake(true);
    const rest = () => setAwake(false);
    const react = () => void bot.current?.play(greeting);
    host.addEventListener("pointerenter", wake);
    host.addEventListener("pointerleave", rest);
    host.addEventListener("focusin", wake);
    host.addEventListener("focusout", rest);
    const self = wrap.current;
    self?.addEventListener("pointerdown", react);
    if (host.tagName !== "FORM") host.addEventListener("click", react);
    return () => {
      host.removeEventListener("pointerenter", wake);
      host.removeEventListener("pointerleave", rest);
      host.removeEventListener("focusin", wake);
      host.removeEventListener("focusout", rest);
      self?.removeEventListener("pointerdown", react);
      host.removeEventListener("click", react);
    };
  }, [greeting, still]);

  // A reply landing earns a nod; the start of thinking plays its scan.
  const previous = useRef(activity);
  useEffect(() => {
    const was = previous.current;
    previous.current = activity;
    if (was === activity || still) return;
    if (activity === "thinking") void bot.current?.play("thinking");
    if (was === "thinking") void bot.current?.play("nod");
  }, [activity, still]);

  return (
    // Clipped sideways so an act that travels never slides over the text beside her.
    <span ref={wrap} className="inline-flex shrink-0 cursor-pointer items-center justify-center [overflow-x:clip]" style={{ width: size }}>
      <BotMascot
        ref={bot}
        label={label}
        mood={MOOD[activity]}
        animate={!still && (awake || activity !== "idle")}
        brand={BRAND}
        size={`${size}px`}
      />
    </span>
  );
}
